import { useMemo } from 'react';
import type { LocationOption } from '../components/registration/types';

interface EventDayNode {
    Date?: string | null;
    DateTimeDescription?: string | null;
}

interface EventNode {
    ID?: string;
    Title?: string;
    RegistrationStatus?: string;
    RegistrationFormTitle?: string;
    Days?: {
        edges?: Array<{ node: EventDayNode }>;
    };
}

interface EventRegisterModule {
    __typename?: string;
    Events?: {
        edges?: Array<{ node: EventNode }>;
    };
}

/**
 * Returns the earliest Day.Date for an event, or null if none available.
 */
function getEarliestDate(event: EventNode): Date | null {
    const days = event.Days?.edges ?? [];
    let earliest: Date | null = null;
    for (const { node } of days) {
        if (!node?.Date) continue;
        const parsed = new Date(node.Date);
        if (isNaN(parsed.getTime())) continue;
        if (!earliest || parsed < earliest) earliest = parsed;
    }
    return earliest;
}

/**
 * Returns active events from EventRegisterModules as LocationOptions,
 * sorted by nearest upcoming date first.
 */
export function useHDELocationOptions(modules: unknown[]): LocationOption[] {
    return useMemo(() => {
        const eventRegisterModules = (modules || []).filter(
            (module): module is EventRegisterModule =>
                typeof module === 'object' &&
                module !== null &&
                '__typename' in module &&
                module.__typename === 'EventRegisterModule',
        );

        if (eventRegisterModules.length === 0) {
            return [];
        }

        const allActiveEvents: EventNode[] = [];

        for (const module of eventRegisterModules) {
            const events = module.Events?.edges || [];
            const activeEvents = events
                .filter(({ node }) => node?.RegistrationStatus === 'active')
                .map(({ node }) => node);

            allActiveEvents.push(...activeEvents);
        }

        const uniqueEvents = Array.from(
            new Map(
                allActiveEvents
                    .filter(
                        (event) =>
                            event.ID &&
                            (event.RegistrationFormTitle || event.Title),
                    )
                    .map((event) => [event.ID, event]),
            ).values(),
        );

        const now = new Date();
        uniqueEvents.sort((a, b) => {
            const dateA = getEarliestDate(a);
            const dateB = getEarliestDate(b);
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;
            const diffA = dateA.getTime() - now.getTime();
            const diffB = dateB.getTime() - now.getTime();
            if (diffA >= 0 && diffB >= 0) return diffA - diffB;
            if (diffA >= 0) return -1;
            if (diffB >= 0) return 1;
            return diffB - diffA;
        });

        return uniqueEvents.map((event) => ({
            label: event.RegistrationFormTitle || event.Title || '',
            value: event.ID!,
        }));
    }, [modules]);
}
